'use client'

import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useRef, useState } from 'react'
import { useI18nMessage, useUpdateI18nMessage } from '@/hooks/use-i18n-messages'
import { I18nMessageForm } from '@/components/partials/i18n-messages'
import { type I18nMessageFormRef } from '@/components/partials/i18n-messages/form'
import { FormSkeleton, ContentError } from '@/components/partials'
import { UpdateI18nMessageParams } from '@/types/i18n-message'
import { FormModeEnum } from '@/enums'

interface UpdateProps {
  id: string
}

const Update = ({ id }: UpdateProps) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const messageFormRef = useRef<I18nMessageFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: message, isLoading, error } = useI18nMessage(id)
  const { mutate: updateI18nMessage, isPending } = useUpdateI18nMessage()

  const handleSubmit = (data: UpdateI18nMessageParams) => {
    updateI18nMessage(
      { messageId: id, params: data },
      {
        onSuccess: async updatedMessage => {
          setIsRedirecting(true)
          queryClient.setQueryData(['i18nMessage', id], updatedMessage)
          await queryClient.invalidateQueries({ queryKey: ['i18nMessages'] })
          toast.success(t('updated_successfully'))
          router.push(`/localization/i18n-messages/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (messageFormRef.current) {
        messageFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('updating_message'))
  }

  const handleCancel = () => {
    router.push(`/localization/i18n-messages/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} />
  }

  if (error || !message) {
    return <ContentError error={error} listUrl="/localization/i18n-messages" />
  }

  return (
    <I18nMessageForm
      ref={messageFormRef}
      mode={FormModeEnum.UPDATE}
      message={message}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
